import type { HtmlAsset } from 'js/src/luminary/html-asset-operations';

import { lintHtmlComposition } from './lintHtmlComposition';
import { canvasSizeOf, captureFrames } from './measure';
import { restingProgressPoints } from './motionContinuityRules';
import { runRuntimeLintRules } from './runtimeRules';
import type { RuntimeLintContext } from './runtimeRules';
import type { GsapTimelineLike, LintFinding } from './types';

/**
 * Lint findings for the composition on `asset`: the static HTML-source rules, plus the runtime rules
 * run against the composition as luminary has it loaded.
 *
 * `sceneDurationMs` is the scene's real playback length in milliseconds (our default time unit); the
 * caller supplies it, because the timeline's own length is the authored one and need not match.
 * Rules needing a real duration no-op without it.
 *
 * `designSystemCss` is the video's design system, for the rules that judge a composition against
 * it. Absent for a video that has none, and those rules then check nothing.
 *
 * `narration` is the voiceover over the scene, in scene seconds, for the rules that judge copy
 * against when it is spoken.
 */
export async function getCompositionLintFindings(
    asset: HtmlAsset,
    sceneDurationMs?: number,
    designSystemCss?: string | null,
    narration?: RuntimeLintContext['narration'] | null,
): Promise<LintFinding[]> {
    const html = asset.html;
    if (!html) {
        return [];
    }
    const staticFindings = lintHtmlComposition(html);
    let runtimeFindings: LintFinding[] | null = null;
    try {
        runtimeFindings = await asset.introspectComposition(async (composition) => {
            const timeline = composition.timeline as GsapTimelineLike | null;
            const frames = await captureFrames(composition, timeline ? undefined : [0]);
            return runRuntimeLintRules({
                document: composition.document,
                timeline,
                sourceHtml: html,
                frames,
                canvasSize: canvasSizeOf(composition.document),
                sceneDurationMs,
                designSystemCss: designSystemCss ?? undefined,
                seek: composition.seek,
                narration: narration ?? undefined,
            });
        });
    } catch (error) {
        // A composition that exists but could not be read. Reporting nothing here is how an
        // unmeasured composition passes for a clean one.
        return [...staticFindings, notMeasured(String(error))];
    }
    // Null is the other thing: there is no composition frame in this environment to measure at all —
    // an asset with no rendered iframe, which is what the source-only callers and the render worker
    // see. Nothing ran, and there is nothing to report, so the source rules stand alone. A
    // composition that *is* loaded and fails to answer throws rather than landing here.
    return [...staticFindings, ...(runtimeFindings ?? [])];
}

/**
 * Where the graphic on `asset` holds still, as scene progress — see `restingProgressPoints`. Null
 * when there is no loaded composition to measure, or its content never rests.
 */
export async function getCompositionRestPoints(
    asset: HtmlAsset,
    sceneDurationMs: number,
): Promise<number[] | null> {
    if (!asset.html) {
        return null;
    }
    const points = await asset.introspectComposition(async (composition) =>
        restingProgressPoints({
            document: composition.document,
            timeline: composition.timeline as GsapTimelineLike | null,
            canvasSize: canvasSizeOf(composition.document),
            sceneDurationMs,
            seek: composition.seek,
        }),
    );
    return points ?? null;
}

function notMeasured(reason: string): LintFinding {
    return {
        ruleId: 'composition-not-measured',
        severity: 'error',
        message:
            `The composition was saved but could not be measured, so none of the rules that judge ` +
            `what it renders have run against it and it is not known to be sound: ${reason}. Try ` +
            `the same edit again — this is a fault in the tooling rather than in the composition, ` +
            `and it usually clears. Do not move on to another scene on the strength of this result.`,
    };
}
